import countries from "../../../countries.json";

// Country rules of the shared address fields (no network, no auth import:
// also used by plain validators such as Stripe's billing-address check).

export const fold = (v) => String(v || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim().toUpperCase();
export const compactPostal = (v) => String(v || "").replace(/[\s-]+/g, "").toUpperCase();

// "US", "us", "United States", "Us" -> "US"; unknown -> "".
const COUNTRY_BY_NAME = new Map(countries.map((c) => [fold(c.name), c.code]));
const COUNTRY_CODES = new Set(countries.map((c) => c.code));
const COUNTRY_ALIASES = { USA: "US", "U.S.A.": "US", "U.S.": "US", "UNITED STATES OF AMERICA": "US", UK: "GB" };
export const countryCodeOf = (value) => {
  const raw = fold(value);
  if (!raw) return "";
  if (raw.length === 2 && COUNTRY_CODES.has(raw)) return raw;
  return COUNTRY_ALIASES[raw] || COUNTRY_BY_NAME.get(raw) || "";
};
export const countryNameOf = (code) => countries.find((c) => c.code === countryCodeOf(code))?.name || "";

// Same list as server_elakay/elakay_services/addressRules.js: countries
// without a postal-code system in general use. Everywhere else it stays
// required for a real address.
export const NO_POSTAL_CODE_COUNTRIES = new Set((
  "AE AG AN AO AW BF BI BJ BO BS BW BZ CD CF CG CI CK CM DJ DM ER FJ GA GD GH GM GQ GY HK HM JM KI KM KN KP " +
  "LY ML MO MR MS MW NR NU QA RW SB SC SL SR SS ST SY TD TF TG TK TL TO TV TZ UG VU YE ZW"
).split(" "));
export const isPostalCodeRequired = (country) => {
  const code = countryCodeOf(country);
  return !code || !NO_POSTAL_CODE_COUNTRIES.has(code);
};

// Countries whose postal code has one fixed national format. Same list as
// server_elakay/elakay_services/addressRules.js POSTAL_CODE_FORMATS; every
// other country only gets POSTAL_PATTERN (letters, digits, spaces, hyphens).
export const POSTAL_PATTERN = /^[A-Za-z0-9 -]+$/;
export const POSTAL_CODE_FORMATS = Object.freeze({
  US: /^\d{5}(?:[ -]?\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
});
export const isValidPostalCode = (country, value) => {
  const postal = String(value ?? "").trim();
  if (!postal || postal.length > 20 || !POSTAL_PATTERN.test(postal)) return false;
  const format = POSTAL_CODE_FORMATS[countryCodeOf(country)];
  return !format || format.test(postal);
};

// What the first administrative level is called in a country (label only).
const STATE_KIND = {
  state: "US AU BR MX IN MY NG VE DE AT FM PW MM SS SD",
  province: "CA AR ES IT NL BE PH ZA CN TR DO CU EC PA KR VN TH ID PK AF IR LK NC",
  region: "FR GB CL PE GH SN CI CM ML NE PL CZ SK DK FI IE NZ GR IS",
  department: "HT CO UY BO PY GT HN SV NI BJ",
  parish: "JM AG BB DM GD KN LC VC AD",
  district: "PT BD NP LU CY UG",
};
const STATE_KIND_BY_COUNTRY = new Map(
  Object.entries(STATE_KIND).flatMap(([kind, codes]) => codes.split(" ").map((code) => [code, kind]))
);
export const stateKindOf = (country) => STATE_KIND_BY_COUNTRY.get(countryCodeOf(country)) || "generic";
// "ZIP code" in the US (and its territories), "Postal code" elsewhere.
const ZIP_COUNTRIES = new Set(["US", "PR", "GU", "VI", "AS", "MP", "UM"]);
export const postalKindOf = (country) => (ZIP_COUNTRIES.has(countryCodeOf(country)) ? "zip" : "postal");
